import { FC, useEffect, useState } from "react"
import BtButton from "../bt-button/bt-button"
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import { ServiceWeeklyDigest } from "../../services/service-weekly-digest";
import { ServiceSupplierInsuranceReminder } from "../../services/service-supplier-insurance-reminder";
import { ServiceSupplierProfileReminders } from "../../services/service-supplier-profile-reminders";
import { ServiceBidReceived, ServiceIntentReceived, ServiceMessageDigest } from "../../services/service-unsubscribe";
import LanguageSwitcher from "../../controls/LanguageSwitcher/LanguageSwitcher";
import styles from "./page-email-unsubscribe.module.scss";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { Loading } from "../../components/shared";

type Params = {
  p: string,
  s: string,
};

type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type UnsubscribeService = {
  unsubscribeEmailNotifications: (args: { p: string, s: string }) => Promise<unknown>;
  fetchStatus: (args: { p: string, s: string }) => Promise<boolean>;
}

type Props = {
  match: RouteTypes['match'];
  service: UnsubscribeService;
  description: string;
}

export const PageEmailUnsubscribe: FC<Props> = ({ match, service, description }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [missingParams, setMissingParams] = useState(false);
  const { p, s } = match.params;

  useEffect(() => {
    (async() => {
      try {
        const status = await service.fetchStatus({ p, s });
        setSubscribed(status !== false);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          if (e.response?.status !== 400) {
            Sentry.captureException(e);
          } else if (e.response.data.error === 'error.missing.parameters') {
            setMissingParams(true);
          }
        }
      } finally {
        setPageLoading(false);
      }
    })();
  }, []);

  const unsubscribe = async() => {
    setLoading(true);
    try {
      await service.unsubscribeEmailNotifications({ p, s });
      setSubscribed(false);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status !== 400) {
        Sentry.captureException(e);
      }
    } finally {
      setLoading(false);
    }
  }

  if (pageLoading) {
    return <Loading type="inline" />
  }

  return (
    <div className={styles.container}>
      <LanguageSwitcher className={styles.languageSwitcher} />
      <div className={styles.content}>
        {missingParams ? (
          <h2>{t('title.missingParams', 'Missing parameters (p, s)')}</h2>
        ) : (
          subscribed ? (
            <>
              <h2>{t('button.subscribed.text', 'Unsubscribe')}</h2>
              <p>{description}</p>
              <BtButton loaderShow={loading} onClick={unsubscribe}>{t('button.text', 'Unsubscribe')}</BtButton>
            </>
            ) : (
            <>
              <h2>{t('button.unsubscribed.text', 'You\'ve been unsubscribed')}</h2>
              <p>{t('description.unsubscribed', 'You will no longer receive this email from us. If this was a mistake, please login and change your preferences in your account.')}</p>
              <BtButton onClick={() => {
                window.location.href = '/';
              }}>{t('button.returnHome', 'Back to Accurator')}</BtButton>
            </>
          )
        )}
      </div>
    </div>
  )
}

// Per-email-type wrappers used by the router.

type RouteOnlyProps = { match: RouteTypes['match'] };

export const PageWeeklyDigestUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceWeeklyDigest}
      description={t('description.subscribed', "We're sorry to see you go. Click the button below to unsubscribe from the weekly digest mailing list.")}
    />
  );
}

export const PageSupplierInsuranceReminderUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceSupplierInsuranceReminder}
      description={t('description.supplierInsuranceReminder', "We're sorry to see you go. Click the button below to stop receiving insurance expiry reminders from Accurator.")}
    />
  );
}

export const PageSupplierProfileReminderUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceSupplierProfileReminders}
      description={t('description.supplierProfileReminder', "We're sorry to see you go. Click the button below to stop receiving profile completion reminders from Accurator.")}
    />
  );
}

export const PageBidReceivedUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceBidReceived}
      description={t('description.bidReceived', "You can manage which notifications you receive at any time from your profile page in Accurator. To stop receiving emails when a supplier submits a bid in your projects, click the button below.")}
    />
  );
}

export const PageIntentReceivedUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceIntentReceived}
      description={t('description.intentReceived', "You can manage which notifications you receive at any time from your profile page in Accurator. To stop receiving emails when a supplier declares their intent to bid, click the button below.")}
    />
  );
}

export const PageMessageDigestUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceMessageDigest}
      description={t('description.messageDigest', "You can manage which notifications you receive at any time from your profile page in Accurator. To stop receiving the daily summary of new supplier messages, click the button below.")}
    />
  );
}
